import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCreateIncident, useClientCompanies, useUsers } from "@crm/api";
import { toast } from "sonner";
import { ArrowLeft, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { lazy, Suspense } from "react";

// Lazy load the location picker
const LocationPicker = lazy(
  () => import("../../../components/maps/LocationPicker")
);

const incidentSchema = z.object({
  title: z.string().min(1, "Title is required").max(255),
  description: z.string().optional(),
  priority: z.enum(["low", "medium", "high", "critical"]),
  category: z.enum([
    "hardware",
    "software",
    "network",
    "security",
    "access",
    "other",
  ]),
  client_company_id: z.string().optional(),
  assigned_to_id: z.string().optional(),
  location_address: z.string().optional(),
  location_latitude: z.number().optional(),
  location_longitude: z.number().optional(),
});

type IncidentFormData = z.infer<typeof incidentSchema>;

export function CreateIncidentPage() {
  const navigate = useNavigate();
  const createMutation = useCreateIncident();
  const { data: companiesData } = useClientCompanies();
  const { data: usersData } = useUsers();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<IncidentFormData>({
    resolver: zodResolver(incidentSchema),
    defaultValues: {
      priority: "medium",
      category: "other",
    },
  });

  const locationData = watch([
    "location_latitude",
    "location_longitude",
    "location_address",
  ]);
  const currentLocation =
    locationData[0] !== undefined && locationData[1] !== undefined
      ? {
          latitude: locationData[0],
          longitude: locationData[1],
          address: locationData[2],
        }
      : null;

  const handleLocationChange = (
    location: { latitude: number; longitude: number; address?: string } | null
  ) => {
    if (location) {
      // Round to 6 decimal places to match backend DecimalField(max_digits=9, decimal_places=6)
      setValue(
        "location_latitude",
        Math.round(location.latitude * 1000000) / 1000000
      );
      setValue(
        "location_longitude",
        Math.round(location.longitude * 1000000) / 1000000
      );
      setValue("location_address", location.address || undefined);
    } else {
      setValue("location_latitude", undefined);
      setValue("location_longitude", undefined);
      setValue("location_address", undefined);
    }
  };

  const onSubmit = async (data: IncidentFormData) => {
    try {
      // Remove empty optional fields and round coordinates
      const payload: any = { ...data };
      if (!payload.client_company_id) delete payload.client_company_id;
      if (!payload.assigned_to_id) delete payload.assigned_to_id;
      if (!payload.location_address) delete payload.location_address;
      if (payload.location_latitude !== undefined) {
        payload.location_latitude =
          Math.round(payload.location_latitude * 1000000) / 1000000;
      } else {
        delete payload.location_latitude;
      }
      if (payload.location_longitude !== undefined) {
        payload.location_longitude =
          Math.round(payload.location_longitude * 1000000) / 1000000;
      } else {
        delete payload.location_longitude;
      }

      const incident = await createMutation.mutateAsync(payload);
      toast.success("Incident created successfully");
      navigate(`/incidents/${incident.id}`);
    } catch (error: any) {
      const message =
        error?.response?.data?.error?.message || "Failed to create incident";
      toast.error(message);
    }
  };

  // Filter staff members who can be assigned
  const assignableStaff =
    usersData?.results?.filter((u) => u.role !== "client") || [];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/incidents"
          className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Incidents
        </Link>
        <h1 className="text-2xl font-semibold text-foreground">
          Create New Incident
        </h1>
      </div>

      {/* Form */}
      <div className="mx-auto max-w-2xl rounded-lg border border-border bg-card p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <label
              htmlFor="title"
              className="text-sm font-medium text-foreground"
            >
              Title <span className="text-destructive">*</span>
            </label>
            <input
              id="title"
              type="text"
              {...register("title")}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              placeholder="Brief description of the issue"
            />
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label
              htmlFor="description"
              className="text-sm font-medium text-foreground"
            >
              Description
            </label>
            <textarea
              id="description"
              rows={4}
              {...register("description")}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              placeholder="Detailed description of the incident..."
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Priority */}
            <div className="space-y-2">
              <label
                htmlFor="priority"
                className="text-sm font-medium text-foreground"
              >
                Priority
              </label>
              <select
                id="priority"
                {...register("priority")}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label
                htmlFor="category"
                className="text-sm font-medium text-foreground"
              >
                Category
              </label>
              <select
                id="category"
                {...register("category")}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="hardware">Hardware</option>
                <option value="software">Software</option>
                <option value="network">Network</option>
                <option value="security">Security</option>
                <option value="access">Access Request</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Client Company */}
            <div className="space-y-2">
              <label
                htmlFor="client_company_id"
                className="text-sm font-medium text-foreground"
              >
                Client Company
              </label>
              <select
                id="client_company_id"
                {...register("client_company_id")}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="">Not assigned to a client</option>
                {companiesData?.results?.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Assign To */}
            <div className="space-y-2">
              <label
                htmlFor="assigned_to_id"
                className="text-sm font-medium text-foreground"
              >
                Assign To
              </label>
              <select
                id="assigned_to_id"
                {...register("assigned_to_id")}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="">Unassigned</option>
                {assignableStaff.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.role})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Location Section */}
          <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Location (Optional)
            </label>
            <p className="text-xs text-muted-foreground">
              Click below to pick a location on the map, or search for an
              address
            </p>
            <Suspense
              fallback={
                <div className="h-20 flex items-center justify-center border border-dashed border-border rounded-md">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              }
            >
              <LocationPicker
                value={currentLocation}
                onChange={handleLocationChange}
              />
            </Suspense>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Link
              to="/incidents"
              className="flex h-10 items-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting || createMutation.isPending}
              className="flex h-10 items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
            >
              {createMutation.isPending ? "Creating..." : "Create Incident"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
